/**
 * `/privacy` — the privacy policy.
 *
 * Authorised by `docs/marketing/app-store-listing.md` §URLs: "**Privacy Policy URL** |
 * Required. §14 risk 1: 'this audience reads the permissions screen.' Plain English, not
 * boilerplate." `apps/web` is named there as the host.
 *
 * THE ORDER OF THIS PAGE IS THE ARGUMENT. A generated policy opens with the controller's
 * legal identity and buries the sensor questions in clause 7. This one opens with the
 * four facts a reader came for — location, audio, photos, and that none of it moves
 * without an account — and puts the legal apparatus after them. product-concept §14 risk
 * 1 is the authority for that inversion: the reader is someone who reads permission
 * dialogues, so the page answers the permission dialogue first.
 *
 * Every claim traces to code. The catalogue block for `privacy.*` lists the file behind
 * each one, and it is the place to check before changing any sentence here — a privacy
 * policy drifting ahead of the binary is the one drift in this repository that is a legal
 * problem rather than a quality one.
 */
import { useTheme } from '../theme/ThemeProvider';
import { useCopy } from '../theme/CopyProvider';
import { PageFrame, Section } from './PageFrame';
import { text } from './prose';

/**
 * The date the policy last changed, and the ONLY place it is written.
 *
 * `privacy.changes.body` promises that a change here changes the date at the top, which
 * is a promise about this constant. Update it in the same commit as any `privacy.*`
 * string, or the page tells the reader something false about itself.
 */
const LAST_UPDATED = '31 July 2026';

/**
 * The recovery-journal window, in days.
 *
 * CLAUDE.md's technical laws: "30-day recovery journal of discarded values". Stated as a
 * slot rather than baked into the string so the number the policy quotes and the number
 * the sync layer implements can be checked against each other.
 */
const RECOVERY_DAYS = 30;

export function PrivacyPage() {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <PageFrame current="/privacy" titleKey="privacy.title">
      <section style={{ display: 'flex', flexDirection: 'column', gap: s.sm }}>
        <p style={{ ...text(theme.type.body), color: c.textSecondary, margin: 0 }}>
          {t('privacy.intro')}
        </p>
        <p style={{ ...text(theme.type.quiet), color: c.textTertiary, margin: 0 }}>
          {t('privacy.updated', { when: LAST_UPDATED })}
        </p>
      </section>

      {/* The short version, as cards rather than prose: these four are the page's
          headline claims, and a reader who stops here has read the true summary. */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: s.md }}>
        <h2
          style={{
            ...text(theme.type.title),
            fontSize: theme.type.title.fontSize * 0.7,
            color: c.text,
            margin: 0,
          }}
        >
          {t('privacy.summary.title')}
        </h2>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: s.sm,
          }}
        >
          {(
            [
              'privacy.summary.location',
              'privacy.summary.audio',
              'privacy.summary.photos',
              'privacy.summary.noAccount',
              'privacy.summary.noTracking',
            ] as const
          ).map((key) => (
            <li
              key={key}
              style={{
                ...text(theme.type.body),
                color: c.textSecondary,
                background: c.surface,
                border: `${s.hairline}px solid ${c.border}`,
                borderRadius: s.radius.md,
                padding: s.cardPadding,
              }}
            >
              {t(key)}
            </li>
          ))}
        </ul>
      </section>

      <Section
        titleKey="privacy.onDevice.title"
        bodyKeys={[
          'privacy.onDevice.location',
          'privacy.onDevice.audio',
          'privacy.onDevice.photos',
          'privacy.onDevice.why',
        ]}
      />

      <Section
        titleKey="privacy.uploads.title"
        bodyKeys={[
          'privacy.uploads.onlySignedIn',
          'privacy.uploads.what',
          'privacy.uploads.transcripts',
          'privacy.uploads.also',
          'privacy.uploads.why',
          'privacy.uploads.where',
        ]}
      />

      <Section
        titleKey="privacy.sharing.title"
        bodyKeys={[
          'privacy.sharing.link',
          'privacy.sharing.visitor',
          'privacy.sharing.visitorId',
          'privacy.sharing.revoke',
        ]}
      />

      <Section
        titleKey="privacy.ai.title"
        bodyKeys={[
          'privacy.ai.what',
          'privacy.ai.route',
          'privacy.ai.optIn',
          'privacy.ai.noProfile',
        ]}
      />

      <Section
        titleKey="privacy.signIn.title"
        bodyKeys={['privacy.signIn.body', 'privacy.signIn.what']}
      />

      <Section
        titleKey="privacy.payments.title"
        bodyKeys={['privacy.payments.body', 'privacy.payments.revenuecat']}
      />

      <Section titleKey="privacy.notifications.title" bodyKeys={['privacy.notifications.body']} />

      <Section
        titleKey="privacy.tracking.title"
        bodyKeys={['privacy.tracking.body', 'privacy.tracking.logs']}
      />

      <Section
        titleKey="privacy.website.title"
        bodyKeys={['privacy.website.body', 'privacy.website.form', 'privacy.website.formWhat']}
      />

      <Section
        titleKey="privacy.keeping.title"
        bodyKeys={['privacy.keeping.appendOnly', 'privacy.keeping.recovery']}
        values={{ days: RECOVERY_DAYS }}
      />

      <Section
        titleKey="privacy.controller.title"
        bodyKeys={[
          'privacy.controller.body',
          'privacy.controller.law',
          // RULINGS R141: the Art. 13 contact detail, named beside the controller's
          // identity. The postal address stays absent — RULINGS R137.
          'privacy.controller.contact',
        ]}
      />

      {/* RULINGS R140: not intended for under-13s, so no child's data is knowingly
          processed. Answers the shared-list question a child's data would otherwise
          raise. Mirrored on `/terms` by `siteTerms.age.*`. No age gate is built. */}
      <Section titleKey="privacy.age.title" bodyKeys={['privacy.age.body']} />

      {/* `privacy.rights.erase` names the in-app control and `privacy.rights.support` keeps the
          support route as the fallback for someone who has lost the device (docs/RULINGS.md
          R134). Between the general statement of rights and the ICO line, because that is the
          order a reader needs them in: what you may ask for, how erasure actually happens, who
          to complain to. */}
      <Section
        titleKey="privacy.rights.title"
        bodyKeys={[
          'privacy.rights.body',
          'privacy.rights.erase',
          'privacy.rights.support',
          'privacy.rights.complain',
        ]}
      />

      <Section titleKey="privacy.changes.title" bodyKeys={['privacy.changes.body']} />
    </PageFrame>
  );
}
